/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';

import { cssMap } from '@atlaskit/css';
import { Anchor, Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { processExcerpt } from '../../utils/search-result-utils';
import { DescriptionBody } from '../description-body';

const styles = cssMap({
	container: {
		paddingTop: token('space.100'),
	},
	commentRow: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: token('space.050'),
	},
	commentLink: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: token('space.050'),
		flex: '1',
		minWidth: '0px',
		textDecoration: 'none',
		color: token('color.text'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	authorName: {
		fontWeight: token('font.weight.medium'),
		color: token('color.text'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
	},
	commentText: {
		color: token('color.text.subtle'),
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		flex: '1',
		minWidth: '0px',
	},
});

/**
 * Validates that a URL is safe to use as an href (https only).
 * Rejects javascript:, data:, and other non-https schemes.
 */
const isValidHttpsUrl = (url: string | null | undefined): url is string => {
	if (!url || typeof url !== 'string') {
		return false;
	}
	try {
		const parsed = new URL(url);
		return parsed.protocol === 'https:';
	} catch {
		return false;
	}
};

export interface EmbeddedComment {
	id?: string;
	/**
	 * The ID of the parent entity — either the document ID (for top-level comments)
	 * or another comment ID (for threaded replies). Used to sort top-level comments
	 * before replies in the embedded comments list.
	 */
	parentId?: string | null;
	excerpt: string | null;
	url?: string | null;
	authorName?: string | null;
}

export interface EmbeddedCommentsProps {
	comments: EmbeddedComment[];
	maxComments?: number;
	/**
	 * When adding expand/collapse for "X more comments", use FormattedMessage for i18n.
	 * Example: <FormattedMessage id="embedded-comments.more" defaultMessage="{count} more comments" values={{ count }} />
	 */
}

/**
 * Renders embedded comments that matched a search query within a document result.
 * Shows author name followed by comment excerpt with highlighted matches.
 * Comments are clickable links when URL is available.
 */
export const EmbeddedComments = ({
	comments,
	maxComments = 3,
}: EmbeddedCommentsProps): React.JSX.Element | null => {
	if (!comments.length) {
		return null;
	}

	// Order top-level comments before threaded replies using parentId.
	// A comment is a reply if its parentId matches another comment's id in the list.
	// This ensures the most contextual comments surface first within the flat list.
	const commentIds = new Set(comments.map((c) => c.id).filter(Boolean));
	const sortedComments = [...comments].sort((a, b) => {
		const aIsReply = a.parentId != null && commentIds.has(a.parentId);
		const bIsReply = b.parentId != null && commentIds.has(b.parentId);
		if (aIsReply === bIsReply) {
			return 0;
		}
		return aIsReply ? 1 : -1;
	});
	const visibleComments = sortedComments.slice(0, maxComments);

	const commentContent = (comment: (typeof visibleComments)[0]) => (
		<Inline xcss={styles.commentRow}>
			{comment.authorName && (
				<Box as="span" xcss={styles.authorName}>
					{comment.authorName}
				</Box>
			)}
			{comment.excerpt && processExcerpt(comment.excerpt) && (
				<Box as="span" xcss={styles.commentText}>
					<DescriptionBody description={processExcerpt(comment.excerpt)} />
				</Box>
			)}
		</Inline>
	);

	return (
		<Box xcss={styles.container}>
			<Stack space="space.050">
				{visibleComments.map((comment, index) =>
					isValidHttpsUrl(comment.url) ? (
						<Anchor
							key={comment.id || index}
							href={comment.url}
							xcss={styles.commentLink}
							target="_blank"
							rel="noopener noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							{commentContent(comment)}
						</Anchor>
					) : (
						<Box key={comment.id || index}>{commentContent(comment)}</Box>
					),
				)}
			</Stack>
		</Box>
	);
};
